
import {defineComponent, PropType} from "vue";
import GradientBg, {GradientVariant} from "@/components/shared/GradientBg.vue";
import {VERT, PROD_FRAGS, GradientLook} from "@/modules/gradientShaders";

// animated backgrounds rendered by a single WebGL fragment shader.
// one quad, one draw call. pauses offscreen, caps dpr at 1.5,
// respects prefers-reduced-motion (renders one still frame),
// falls back to the CSS GradientBg when WebGL is unavailable.

// mesh   — flowing color mesh (Stripe-hero class, all-purpose)
// orb    — luminous bokeh discs in deep space
// waves  — silky light lines rippling over the gradient
// rays   — v1 sin-stripe shafts (the one the site uses)
// flag   — rays v2: the ray field waves like a flag pinned on the left
// banner — rays v3: heavy silk banner, broad bands, crest sheen
// pennant— rays v4: streamers fluttering along the ray direction, two depths
// beam   — one dominant cinematic light beam from the corner
// shafts — noise-driven irregular light shafts (rays v2)
// veil   — ultra-soft overlapping light veils, barely-there
// silk   — domain-warped fabric folds with a moving sheen
// halo   — one large breathing ring of light, off-center
// dune   — layered dark hills with rim light, horizon depth
// nebula — soft fbm cloud field
// ribbon   — one flowing shaded band crossing the frame
// grid     — perspective light grid fading to the horizon
// particles— drifting light dust in layered depth
// eclipse  — dark disc with a travelling rim glow
// polar    — aurora curtains of light
// caustics — underwater light web
// pulse    — soft radial waves from an off-center source
// strands  — hanging light threads with travelling glints
// horizon  — minimal glowing horizon line
// mist     — drifting fog banks
// smoke    — rising wisps
// lantern  — light source glowing through fog
// topo     — topographic contour lines
// circuit  — tech lines with travelling pulses
// orbits   — elliptical paths with moving lights
// flare    — anamorphic lens streak
// embers   — rising light sparks
// surge    — flowing energy line
// sigil    — the iconomy y as a breathing edge-glow light form (right third)
// draw     — the y writes itself once (pen nib rides the strokes), then breathes
// ignite   — the y draws itself, then its interior fills with light
// plot     — the y plotted as a thin technical line, crosshair pen tip
// sign     — the y writes itself, holds, fades and rewrites — a looping signature
export type {GradientLook};

function hex(c: string): Array<number> {
  return [
    parseInt(c.slice(1, 3), 16) / 255,
    parseInt(c.slice(3, 5), 16) / 255,
    parseInt(c.slice(5, 7), 16) / 255
  ];
}

// palettes from colors.sass tokens — hex exported so demo pages can seed color editors
export const VARIANT_HEX: Record<GradientVariant, Array<string>> = {
  // brighter default with clean steps: royal blue -> sky -> pale -> ice
  azure: ["#3068ED", "#78c0e0", "#bae0ef", "#DEF1F7"],
  royal: ["#16184F", "#1654a9", "#78c0e0", "#12133e"],
  deep:  ["#0b0d28", "#16184F", "#1654a9", "#0b0d28"],
  night: ["#050616", "#12133e", "#16184F", "#1654a9"],
  // night base, dark ember mid, deep orange light, the accent on the crests —
  // light slot held below the accent so white copy stays ≥ 4.5:1 on the rays
  ember: ["#050616", "#2a1008", "#b53f00", "#E65100"]
};

const PALETTES: Record<GradientVariant, Array<Array<number>>> = {
  azure: VARIANT_HEX.azure.map(hex),
  royal: VARIANT_HEX.royal.map(hex),
  deep: VARIANT_HEX.deep.map(hex),
  night: VARIANT_HEX.night.map(hex),
  ember: VARIANT_HEX.ember.map(hex)
};

const DEFAULT_ANGLE: Partial<Record<GradientLook, number>> = {
  rays: -30, flag: -30, banner: -30, pennant: -30, beam: -27, shafts: -35, veil: 0
};

export default defineComponent({
  name: "GradientBg3D",
  components: {GradientBg},
  props: {
    variant: {type: String as PropType<GradientVariant>, default: "azure"},
    look: {type: String as PropType<GradientLook>, default: "mesh"},
    // light direction in degrees for the ray family (rays/beam/shafts/veil);
    // null = per-look default
    angle: {type: Number as PropType<number | null>, default: null},
    // layout seed for mesh — different seeds give different anchor arrangements
    seed: {type: Number, default: 1},
    // optional palette override: 4 hex colors replacing the variant palette
    colors: {type: Array as PropType<Array<string> | null>, default: null},
    animated: {type: Boolean, default: true}
  },
  data() {
    return {
      glOk: true,
      cvKey: 0,
      gl: null as WebGLRenderingContext | null,
      uniforms: {} as Record<string, WebGLUniformLocation | null>,
      rafId: 0,
      lastFrame: 0,
      visible: true,
      reducedMotion: false,
      startStamp: 0,
      catalogFrags: null as Partial<Record<GradientLook, string>> | null,
      resizeObs: null as ResizeObserver | null,
      intersectObs: null as IntersectionObserver | null,
      onVisibility: null as (() => void) | null
    }
  },
  watch: {
    variant() {
      this.setColors();
      if(!this.running) this.renderFrame();
    },
    colors() {
      this.setColors();
      if(!this.running) this.renderFrame();
    },
    angle() {
      this.setStatics();
      if(!this.running) this.renderFrame();
    },
    seed() {
      this.setStatics();
      if(!this.running) this.renderFrame();
    },
    look() {
      this.ensureFrag().then(() => {
        if(!this.gl) return;
        if(!this.buildProgram()) { this.glOk = false; return; }
        this.setColors();
        this.resize(true);
        this.renderFrame();
      });
    },
    animated() {
      this.updateLoop();
    }
  },
  computed: {
    running(): boolean {
      return this.animated && this.visible && !this.reducedMotion;
    }
  },
  mounted() {
    this.reducedMotion = window.matchMedia("(prefers-reduced-motion: reduce)").matches;
    this.ensureFrag().then(() => {
      if(!this.initGL()) { this.glOk = false; return; }

      this.startStamp = performance.now();
      this.setColors();
      this.resize(true);   // force: guarantees the first uRes upload (never skip it → no /0 = black)
      this.renderFrame();

      const canvas = this.$refs.cv as HTMLCanvasElement;

      this.resizeObs = new ResizeObserver(() => { this.resize(); if(!this.running) this.renderFrame(); });
      this.resizeObs.observe(canvas.parentElement as HTMLElement);

      this.intersectObs = new IntersectionObserver((entries) => {
        this.visible = entries[0].isIntersecting;
        this.updateLoop();
      });
      this.intersectObs.observe(canvas);

      this.onVisibility = () => this.updateLoop();
      document.addEventListener("visibilitychange", this.onVisibility);

      this.updateLoop();
    });
  },
  activated() {
    if(this.gl || !this.glOk) return;   // first activation right after mounted
    this.cvKey++;                        // fresh canvas — the old context is lost
    this.$nextTick(() => {
      if(!this.initGL()){ this.glOk = false; return; }
      this.setColors();
      this.resize(true);
      this.renderFrame();
      const canvas = this.$refs.cv as HTMLCanvasElement;
      if(this.intersectObs){ this.intersectObs.disconnect(); this.intersectObs.observe(canvas); }
      this.updateLoop();
    });
  },
  deactivated() {
    window.cancelAnimationFrame(this.rafId);
    this.rafId = 0;
    if(this.gl){
      this.gl.getExtension("WEBGL_lose_context")?.loseContext();
      this.gl = null;
    }
  },
  beforeUnmount() {
    window.cancelAnimationFrame(this.rafId);
    this.rafId = 0;
    if(this.resizeObs) this.resizeObs.disconnect();
    if(this.intersectObs) this.intersectObs.disconnect();
    if(this.onVisibility) document.removeEventListener("visibilitychange", this.onVisibility);
    if(this.gl) this.gl.getExtension("WEBGL_lose_context")?.loseContext();
  },
  methods: {
    // production looks ship inline; anything else pulls the demo catalog once
    async ensureFrag(): Promise<void> {
      if(PROD_FRAGS[this.look as GradientLook] || this.catalogFrags) return;
      try {
        const catalog = await import("@/modules/gradientShaderCatalog");
        this.catalogFrags = catalog.CATALOG_FRAGS;
      } catch (err) {
        console.log(err);
      }
    },
    initGL(): boolean {
      const canvas = this.$refs.cv as HTMLCanvasElement;
      const gl = canvas.getContext("webgl", {antialias: false, alpha: false, powerPreference: "low-power"});
      if(!gl) return false;

      // fullscreen quad, shared by every program
      gl.bindBuffer(gl.ARRAY_BUFFER, gl.createBuffer());
      gl.bufferData(gl.ARRAY_BUFFER, new Float32Array([-1, -1, 3, -1, -1, 3]), gl.STATIC_DRAW);

      this.gl = gl;
      return this.buildProgram();
    },
    buildProgram(): boolean {
      const gl = this.gl as WebGLRenderingContext;
      const look = this.look as GradientLook;
      const src = PROD_FRAGS[look] ?? this.catalogFrags?.[look] ?? (PROD_FRAGS.mesh as string);

      const compile = (type: number, source: string) => {
        const shader = gl.createShader(type) as WebGLShader;
        gl.shaderSource(shader, source);
        gl.compileShader(shader);
        return shader;
      };

      const program = gl.createProgram() as WebGLProgram;
      gl.attachShader(program, compile(gl.VERTEX_SHADER, VERT));
      gl.attachShader(program, compile(gl.FRAGMENT_SHADER, src));
      gl.linkProgram(program);
      if(!gl.getProgramParameter(program, gl.LINK_STATUS)) return false;
      gl.useProgram(program);

      const aPos = gl.getAttribLocation(program, "aPos");
      gl.enableVertexAttribArray(aPos);
      gl.vertexAttribPointer(aPos, 2, gl.FLOAT, false, 0, 0);

      for(const name of ["uRes", "uTime", "uAngle", "uSeed", "uColA", "uColB", "uColC", "uColD"]){
        this.uniforms[name] = gl.getUniformLocation(program, name);
      }
      return true;
    },
    setColors() {
      if(!this.gl) return;
      const custom = this.colors;
      const pal = (custom && custom.length === 4)
        ? custom.map(hex)
        : (PALETTES[this.variant as GradientVariant] ?? PALETTES.azure);
      const keys = ["uColA", "uColB", "uColC", "uColD"];
      for(let i = 0; i < 4; i++){
        this.gl.uniform3fv(this.uniforms[keys[i]], pal[i]);
      }
      this.setStatics();
    },
    setStatics() {
      if(!this.gl) return;
      const deg = this.angle ?? DEFAULT_ANGLE[this.look as GradientLook] ?? 0;
      this.gl.uniform1f(this.uniforms.uAngle, deg * Math.PI / 180);
      this.gl.uniform1f(this.uniforms.uSeed, this.seed);
    },
    resize(force = false) {
      const canvas = this.$refs.cv as HTMLCanvasElement;
      if(!this.gl || !canvas.parentElement) return;
      const dpr = Math.min(window.devicePixelRatio || 1, 1.5);
      const w = Math.max(1, Math.round(canvas.parentElement.clientWidth * dpr));
      const h = Math.max(1, Math.round(canvas.parentElement.clientHeight * dpr));
      // only the canvas-buffer resize (which clears the canvas) is skippable;
      // viewport + uRes must always be uploaded, or the shader divides by a stale
      // uRes (0 on a fresh program) and renders black
      if(force || canvas.width !== w || canvas.height !== h) {
        canvas.width = w;
        canvas.height = h;
      }
      this.gl.viewport(0, 0, w, h);
      this.gl.uniform2f(this.uniforms.uRes, w, h);
    },
    renderFrame() {
      if(!this.gl) return;
      // still frames get a fixed mid-animation time, so they never look empty
      const t = this.reducedMotion ? 40 : (performance.now() - this.startStamp) / 1000;
      this.gl.uniform1f(this.uniforms.uTime, t);
      this.gl.drawArrays(this.gl.TRIANGLES, 0, 3);
    },
    updateLoop() {
      window.cancelAnimationFrame(this.rafId);
      this.rafId = 0;
      if(!this.gl) return;
      if(this.running && document.visibilityState === "visible") this.tick();
      else this.renderFrame();
    },
    tick() {
      // ~30fps budget: every shader runs a 0.1–0.26x time base, so half rate
      // is visually indistinguishable and halves GPU/battery cost
      this.rafId = window.requestAnimationFrame((now) => {
        if(now - this.lastFrame >= 32){
          this.lastFrame = now;
          this.renderFrame();
        }
        this.tick();
      });
    }
  }
});
